/**
 * SortPanel.jsx
 * ─────────────
 * Multi-level sort configuration panel for GridView.
 * Renders inline below the grid toolbar (not a modal/drawer).
 * Changes apply immediately — no Apply button needed.
 */

import React from 'react';

const MAX_SORT_LEVELS = 5;

export default function SortPanel({ columns, sortKeys, onSortChange }) {
  const usedFields = new Set(sortKeys.map(sk => sk.field));

  function addLevel() {
    if (sortKeys.length >= MAX_SORT_LEVELS) return;
    const available = columns.find(c => !usedFields.has(c.key));
    if (!available) return;
    onSortChange([...sortKeys, { field: available.key, direction: 'asc' }]);
  }

  function removeLevel(idx) {
    onSortChange(sortKeys.filter((_, i) => i !== idx));
  }

  function changeField(idx, newField) {
    const next = sortKeys.map((sk, i) =>
      i === idx ? { ...sk, field: newField } : sk
    );
    onSortChange(next);
  }

  function toggleDirection(idx) {
    const next = sortKeys.map((sk, i) =>
      i === idx ? { ...sk, direction: sk.direction === 'asc' ? 'desc' : 'asc' } : sk
    );
    onSortChange(next);
  }

  function clearAll() {
    onSortChange([]);
  }

  const canAdd = sortKeys.length < MAX_SORT_LEVELS &&
    columns.some(c => !usedFields.has(c.key));

  return (
    <div className="gv-sort-panel">
      {sortKeys.length === 0 ? (
        <div className="gv-sort-empty">
          No sort applied. Click "Add sort level" or click a column header to sort.
        </div>
      ) : (
        <div className="gv-sort-list">
          {sortKeys.map((sk, idx) => (
            <div key={idx} className="gv-sort-row">
              <span className="gv-sort-drag-handle">&#10495;</span>
              <span className="gv-sort-row-label">
                {idx === 0 ? 'Sort by' : 'Then by'}
              </span>
              <select
                className="gv-sort-field-select"
                value={sk.field}
                onChange={e => changeField(idx, e.target.value)}
              >
                {columns.map(col => (
                  <option
                    key={col.key}
                    value={col.key}
                    disabled={usedFields.has(col.key) && col.key !== sk.field}
                  >
                    {col.label}
                  </option>
                ))}
              </select>
              <div className="gv-sort-dir-toggle">
                <button
                  type="button"
                  className={`gv-sort-dir-btn${sk.direction === 'asc' ? ' active' : ''}`}
                  onClick={() => { if (sk.direction !== 'asc') toggleDirection(idx); }}
                >
                  A&#8594;Z
                </button>
                <button
                  type="button"
                  className={`gv-sort-dir-btn${sk.direction === 'desc' ? ' active' : ''}`}
                  onClick={() => { if (sk.direction !== 'desc') toggleDirection(idx); }}
                >
                  Z&#8594;A
                </button>
              </div>
              <button
                type="button"
                className="gv-sort-remove-btn"
                onClick={() => removeLevel(idx)}
                aria-label="Remove sort level"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="gv-sort-actions">
        <button
          type="button"
          className="lrc-btn gv-sort-add-btn"
          onClick={addLevel}
          disabled={!canAdd}
        >
          + Add sort level
        </button>
        {sortKeys.length > 0 && (
          <button
            type="button"
            className="gv-sort-clear-btn"
            onClick={clearAll}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
